import React, { useState } from 'react';
import { API_BASE } from '../utils/api';

interface TestResult {
  test_name: string;
  status: 'pass' | 'fail' | 'pending' | 'error';
  message: string;
  duration?: number;
  details?: any;
}

interface WorkflowTestingProps {
  onClose: () => void;
}

const WorkflowTesting: React.FC<WorkflowTestingProps> = ({ onClose }) => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [running, setRunning] = useState(false);
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');

  const availableTests = [
    { id: 'workflow_creation', name: 'Workflow Creation', description: 'Test creating new workflows' },
    { id: 'workflow_transition', name: 'Workflow Transitions', description: 'Test workflow stage transitions' },
    { id: 'task_management', name: 'Task Management', description: 'Test task creation and updates' },
    { id: 'bulk_operations', name: 'Bulk Operations', description: 'Test bulk workflow operations' },
    { id: 'notifications', name: 'Notifications', description: 'Test notification system' },
    { id: 'sla_management', name: 'SLA Management', description: 'Test SLA calculations and alerts' },
    { id: 'analytics', name: 'Analytics', description: 'Test analytics data accuracy' },
    { id: 'templates', name: 'Templates', description: 'Test workflow templates' },
    { id: 'automation', name: 'Automation', description: 'Test automation rules' },
    { id: 'api_endpoints', name: 'API Endpoints', description: 'Test all API endpoints' }
  ];

  const runTest = async (testId: string): Promise<TestResult> => {
    const startTime = Date.now();
    
    try {
      switch (testId) {
        case 'workflow_creation':
          return await testWorkflowCreation(startTime);
        case 'workflow_transition':
          return await testWorkflowTransition(startTime);
        case 'task_management':
          return await testTaskManagement(startTime);
        case 'bulk_operations':
          return await testBulkOperations(startTime);
        case 'notifications':
          return await testNotifications(startTime);
        case 'sla_management':
          return await testSLAManagement(startTime);
        case 'analytics':
          return await testAnalytics(startTime);
        case 'templates':
          return await testTemplates(startTime);
        case 'automation':
          return await testAutomation(startTime);
        case 'api_endpoints':
          return await testAPIEndpoints(startTime);
        default:
          return {
            test_name: testId,
            status: 'error',
            message: 'Unknown test',
            duration: Date.now() - startTime
          };
      }
    } catch (err: any) {
      return {
        test_name: testId,
        status: 'error',
        message: err.message || 'Test failed with error',
        duration: Date.now() - startTime
      };
    }
  };

  const testWorkflowCreation = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'Workflow Creation',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      // Test creating a workflow
      const response = await fetch(`${API_BASE}/workflow/cases/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adverse_event_id: 1,
          priority_level: 'normal'
        })
      });

      if (response.ok) {
        return {
          test_name: 'Workflow Creation',
          status: 'pass',
          message: 'Workflow creation test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'Workflow Creation',
          status: 'fail',
          message: `Workflow creation failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'Workflow Creation',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testWorkflowTransition = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'Workflow Transitions',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      // Test workflow transition
      const response = await fetch(`${API_BASE}/workflow/cases/1/transition`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_stage: 'in_review',
          transition_reason: 'Test transition'
        })
      });

      if (response.ok) {
        return {
          test_name: 'Workflow Transitions',
          status: 'pass',
          message: 'Workflow transition test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'Workflow Transitions',
          status: 'fail',
          message: `Workflow transition failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'Workflow Transitions',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testTaskManagement = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'Task Management',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      // Test task creation
      const response = await fetch(`${API_BASE}/workflow/tasks/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workflow_id: 1,
          task_type: 'safety_review',
          title: 'Test Task',
          description: 'Test task description',
          priority: 'normal'
        })
      });

      if (response.ok) {
        return {
          test_name: 'Task Management',
          status: 'pass',
          message: 'Task management test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'Task Management',
          status: 'fail',
          message: `Task management failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'Task Management',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testBulkOperations = async (startTime: number): Promise<TestResult> => {
    // Mock test for bulk operations
    return {
      test_name: 'Bulk Operations',
      status: 'pass',
      message: 'Bulk operations test passed (mock)',
      duration: Date.now() - startTime
    };
  };

  const testNotifications = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'Notifications',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      const response = await fetch(`${API_BASE}/workflow/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return {
          test_name: 'Notifications',
          status: 'pass',
          message: 'Notifications test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'Notifications',
          status: 'fail',
          message: `Notifications test failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'Notifications',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testSLAManagement = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'SLA Management',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      const response = await fetch(`${API_BASE}/workflow/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return {
          test_name: 'SLA Management',
          status: 'pass',
          message: 'SLA management test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'SLA Management',
          status: 'fail',
          message: `SLA management test failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'SLA Management',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testAnalytics = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'Analytics',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      const response = await fetch(`${API_BASE}/workflow/analytics/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return {
          test_name: 'Analytics',
          status: 'pass',
          message: 'Analytics test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'Analytics',
          status: 'fail',
          message: `Analytics test failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'Analytics',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testTemplates = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'Templates',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      const response = await fetch(`${API_BASE}/workflow/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return {
          test_name: 'Templates',
          status: 'pass',
          message: 'Templates test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'Templates',
          status: 'fail',
          message: `Templates test failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'Templates',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testAutomation = async (startTime: number): Promise<TestResult> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          test_name: 'Automation',
          status: 'fail',
          message: 'No authentication token found',
          duration: Date.now() - startTime
        };
      }

      const response = await fetch(`${API_BASE}/workflow/automation/rules`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return {
          test_name: 'Automation',
          status: 'pass',
          message: 'Automation test passed',
          duration: Date.now() - startTime
        };
      } else {
        return {
          test_name: 'Automation',
          status: 'fail',
          message: `Automation test failed: ${response.status}`,
          duration: Date.now() - startTime
        };
      }
    } catch (err: any) {
      return {
        test_name: 'Automation',
        status: 'error',
        message: err.message,
        duration: Date.now() - startTime
      };
    }
  };

  const testAPIEndpoints = async (startTime: number): Promise<TestResult> => {
    const endpoints = [
      '/workflow/analytics/overview',
      '/workflow/cases/',
      '/workflow/tasks/',
      '/workflow/notifications',
      '/workflow/settings',
      '/workflow/templates',
      '/workflow/automation/rules'
    ];

    let passed = 0;
    let failed = 0;

    for (const endpoint of endpoints) {
      try {
        const token = localStorage.getItem('token');
        if (!token) continue;

        const response = await fetch(`${API_BASE}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          passed++;
        } else {
          failed++;
        }
      } catch (err) {
        failed++;
      }
    }

    return {
      test_name: 'API Endpoints',
      status: failed === 0 ? 'pass' : 'fail',
      message: `${passed} endpoints passed, ${failed} failed`,
      duration: Date.now() - startTime
    };
  };

  const runSelectedTests = async () => {
    if (selectedTests.length === 0) {
      setError('Please select at least one test to run');
      return;
    }

    setRunning(true);
    setTestResults([]);
    setError('');

    const results: TestResult[] = [];

    for (const testId of selectedTests) {
      const test = availableTests.find(t => t.id === testId);
      if (!test) continue;

      // Add pending result
      const pendingResult: TestResult = {
        test_name: test.name,
        status: 'pending',
        message: 'Running...'
      };
      setTestResults(prev => [...prev, pendingResult]);

      // Run the test
      const result = await runTest(testId);
      results.push(result);

      // Update results
      setTestResults(prev => prev.map(r => 
        r.test_name === test.name ? result : r
      ));
    }

    setRunning(false);
    setSuccess(`Completed ${results.length} tests`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const runAllTests = async () => {
    setSelectedTests(availableTests.map(t => t.id));
    await runSelectedTests();
  };

  const toggleTest = (testId: string) => {
    setSelectedTests(prev => 
      prev.includes(testId) 
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
        return 'text-green-600 bg-green-100';
      case 'fail':
        return 'text-red-600 bg-red-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return '✓';
      case 'fail':
        return '✗';
      case 'error':
        return '⚠';
      case 'pending':
        return '⏳';
      default:
        return '?';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Workflow Testing Suite</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Test Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Tests</h3>
            <div className="space-y-3">
              {availableTests.map((test) => (
                <label key={test.id} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedTests.includes(test.id)}
                    onChange={() => toggleTest(test.id)}
                    className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{test.name}</div>
                    <div className="text-sm text-gray-500">{test.description}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-6 space-y-3">
              <button
                onClick={runSelectedTests}
                disabled={running || selectedTests.length === 0}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {running ? 'Running Tests...' : 'Run Selected Tests'}
              </button>
              <button
                onClick={runAllTests}
                disabled={running}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                Run All Tests
              </button>
            </div>
          </div>

          {/* Test Results */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testResults.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No tests run yet. Select tests and click "Run Selected Tests".
                </div>
              ) : (
                testResults.map((result, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(result.status)}`}>
                          {getStatusIcon(result.status)} {result.status.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{result.test_name}</span>
                      </div>
                      {result.duration && (
                        <span className="text-xs text-gray-500">{result.duration}ms</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{result.message}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTesting;

